import type { DailyCount } from "@/lib/types";
import { useCallback, useRef, useState } from "react";

interface Props {
  entities: DailyCount[];
  groups: DailyCount[];
  messages: DailyCount[];
}

const COLORS = {
  entities: { stroke: "var(--iris-neon-magenta)", label: "Entities" },
  groups: { stroke: "var(--iris-neon-cyan)", label: "Groups" },
  messages: { stroke: "var(--iris-neon-purple)", label: "Messages" },
} as const;

function cumulativeCounts(daily: DailyCount[]): number[] {
  const sorted = [...daily].sort((a, b) => a.date.localeCompare(b.date));
  let sum = 0;
  return sorted.map((d) => {
    sum += d.count;
    return sum;
  });
}

function buildPath(values: number[], width: number, height: number, max: number): string {
  if (values.length < 2 || max === 0) return "";
  const step = width / (values.length - 1);
  return values
    .map((v, i) => {
      const x = i * step;
      const y = height - (v / max) * height;
      return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
}

function fmtDate(d: string) {
  if (!d) return "";
  const dt = new Date(d);
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function fmtValue(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return String(value);
}

export function GrowthChart({ entities, groups, messages }: Props) {
  const eCum = cumulativeCounts(entities);
  const gCum = cumulativeCounts(groups);
  const mCum = cumulativeCounts(messages);

  const allMax = Math.max(...eCum, ...gCum, ...mCum, 1);

  const W = 280;
  const H = 140;

  const ePath = buildPath(eCum, W, H, allMax);
  const gPath = buildPath(gCum, W, H, allMax);
  const mPath = buildPath(mCum, W, H, allMax);

  const dates = [...entities].sort((a, b) => a.date.localeCompare(b.date));
  const dailyDates = [...entities].sort((a, b) => a.date.localeCompare(b.date));
  const dailyEntities = dailyDates.map((d) => d.count);
  const dailyGroups = [...groups].sort((a, b) => a.date.localeCompare(b.date)).map((d) => d.count);
  const dailyMessages = [...messages]
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((d) => d.count);

  const firstDate = dates[0]?.date ?? "";
  const lastDate = dates[dates.length - 1]?.date ?? "";
  const midDate = dates[Math.floor(dates.length / 2)]?.date ?? "";

  // Hover state
  const svgRef = useRef<SVGSVGElement>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const onMouseMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      const svg = svgRef.current;
      if (!svg || dates.length < 2) return;
      const rect = svg.getBoundingClientRect();
      const relX = e.clientX - rect.left;
      const svgWidth = rect.width;
      // Map mouse X to SVG coordinate space (accounting for viewBox padding)
      const viewBoxWidth = W + 20;
      const svgX = (relX / svgWidth) * viewBoxWidth - 10;
      const step = W / (dates.length - 1);
      const idx = Math.round(Math.max(0, Math.min(dates.length - 1, svgX / step)));
      setHoverIdx(idx);
    },
    [dates.length],
  );

  const onMouseLeave = useCallback(() => setHoverIdx(null), []);

  // CSS var() works in standard SVG stroke attributes.
  const STROKE_HEX = {
    entities: "var(--iris-neon-magenta)",
    groups: "var(--iris-neon-cyan)",
    messages: "var(--iris-neon-purple)",
  } as const;

  return (
    <div className="iris-card" style={{ padding: 24, display: "flex", flexDirection: "column" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 16,
        }}
      >
        <h3 className="iris-h3" style={{ margin: 0 }}>
          Data Growth
        </h3>
        {firstDate && lastDate && (
          <span className="iris-meta">
            {fmtDate(firstDate)} &ndash; {fmtDate(lastDate)}
          </span>
        )}
      </div>

      <div
        style={{
          position: "relative",
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {ePath ? (
          <svg
            ref={svgRef}
            viewBox={`-10 -10 ${W + 20} ${H + 30}`}
            style={{ width: "100%", overflow: "visible", height: 160 }}
            preserveAspectRatio="xMidYMid meet"
            aria-label="Data growth chart"
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
          >
            <title>Data growth chart</title>
            {/* Grid lines */}
            {[0, 0.25, 0.5, 0.75, 1].map((frac) => (
              <line
                key={frac}
                x1={0}
                x2={W}
                y1={H - frac * H}
                y2={H - frac * H}
                stroke="rgba(255,255,255,0.04)"
                strokeWidth={1}
              />
            ))}
            {/* Lines */}
            <path
              d={mPath}
              fill="none"
              stroke={STROKE_HEX.messages}
              strokeWidth={2}
              opacity={0.8}
            />
            <path d={gPath} fill="none" stroke={STROKE_HEX.groups} strokeWidth={2} opacity={0.8} />
            <path d={ePath} fill="none" stroke={STROKE_HEX.entities} strokeWidth={2.5} />

            {/* Crosshair + tooltip */}
            {hoverIdx != null && dates.length > 0 && (
              <>
                {/* Vertical line */}
                <line
                  x1={(hoverIdx * W) / (dates.length - 1)}
                  x2={(hoverIdx * W) / (dates.length - 1)}
                  y1={0}
                  y2={H}
                  stroke="rgba(255,255,255,0.25)"
                  strokeWidth={1}
                  strokeDasharray="3 3"
                />
                {/* Dots on each line */}
                {[
                  { cum: eCum, color: STROKE_HEX.entities },
                  { cum: gCum, color: STROKE_HEX.groups },
                  { cum: mCum, color: STROKE_HEX.messages },
                ].map(({ cum, color }) =>
                  cum[hoverIdx] != null ? (
                    <circle
                      key={color}
                      cx={(hoverIdx * W) / (dates.length - 1)}
                      cy={H - (cum[hoverIdx] / allMax) * H}
                      r={3}
                      fill={color}
                      stroke="var(--iris-bg-0)"
                      strokeWidth={1.5}
                    />
                  ) : null,
                )}
                {/* Tooltip background + text */}
                <foreignObject
                  x={Math.min((hoverIdx * W) / (dates.length - 1) + 8, W - 100)}
                  y={-8}
                  width={105}
                  height={68}
                  style={{ pointerEvents: "none" }}
                >
                  <div
                    style={{
                      background: "var(--iris-bg-3)",
                      border: "1px solid var(--iris-line-2)",
                      borderRadius: "var(--iris-r-sm)",
                      padding: "5px 8px",
                      fontSize: "var(--iris-t-micro)",
                      lineHeight: 1.5,
                      color: "var(--iris-fg-3)",
                    }}
                  >
                    <div style={{ color: "var(--iris-fg-1)", fontWeight: 600, marginBottom: 2 }}>
                      {fmtDate(dates[hoverIdx]?.date ?? "")}
                    </div>
                    <div style={{ color: STROKE_HEX.entities }}>
                      Entities: {dailyEntities[hoverIdx]?.toLocaleString() ?? 0}
                    </div>
                    <div style={{ color: STROKE_HEX.groups }}>
                      Groups: {dailyGroups[hoverIdx]?.toLocaleString() ?? 0}
                    </div>
                    <div style={{ color: STROKE_HEX.messages }}>
                      Msgs: {dailyMessages[hoverIdx]?.toLocaleString() ?? 0}
                    </div>
                  </div>
                </foreignObject>
              </>
            )}

            {/* Date labels */}
            <text x={0} y={H + 18} fill="var(--iris-fg-4)" fontSize={10}>
              {fmtDate(firstDate)}
            </text>
            <text x={W / 2} y={H + 18} fill="var(--iris-fg-4)" fontSize={10} textAnchor="middle">
              {fmtDate(midDate)}
            </text>
            <text x={W} y={H + 18} fill="var(--iris-fg-4)" fontSize={10} textAnchor="end">
              {fmtDate(lastDate)}
            </text>
          </svg>
        ) : (
          <p className="iris-small">No activity data yet</p>
        )}
      </div>

      {/* Legend */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: 8,
          marginTop: 16,
        }}
      >
        {[
          { key: "entities" as const, val: fmtValue(eCum[eCum.length - 1] ?? 0) },
          { key: "groups" as const, val: fmtValue(gCum[gCum.length - 1] ?? 0) },
          { key: "messages" as const, val: fmtValue(mCum[mCum.length - 1] ?? 0) },
        ].map((s) => {
          const cfg = COLORS[s.key];
          return (
            <LegendEntry key={s.key} label={cfg.label} color={STROKE_HEX[s.key]} value={s.val} />
          );
        })}
      </div>
    </div>
  );
}

function LegendEntry({
  label,
  color,
  value,
}: {
  label: string;
  color: string;
  value: string;
}) {
  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          color: "var(--iris-fg-3)",
          fontSize: "var(--iris-t-small)",
        }}
      >
        <span
          style={{
            width: 16,
            height: 2,
            background: color,
            borderRadius: 2,
            flexShrink: 0,
          }}
        />
        {label}
      </div>
      <span
        style={{
          color: "var(--iris-fg-1)",
          fontFamily: "var(--iris-font-mono)",
          fontSize: "var(--iris-t-small)",
        }}
      >
        {value}
      </span>
    </>
  );
}
